import React, { useState } from 'react';
import { useCustomerTheme } from '../Theme';
import { useTranslation } from 'react-i18next';

import { Forward, X } from 'lucide-react';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { chipResourceKey } from './chipResourceKey';
import { useResolvedResourceContent } from './useResolvedResourceContent';

/**
 * P3c — post-response action chips.
 *
 * tri-server emits an `actionChips` resource after a turn: the tenant's own tools ranked for
 * the current focus entity, each bound to that entity. We render them as tappable pills; a
 * tap re-sends the bound context pinned to the chosen tool's `offer` (via `submitInput`'s
 * `exclusiveToolId` option), so the action routes deterministically. Modeled on
 * {@link FollowUpSuggestionsResource} / {@link ToolChoicesResource}.
 */
interface ActionChip {
    label: string;
    toolId: string;
    offer: string;
    prompt: string;
    boundContext?: string;
}

interface ActionChipsContent {
    language?: string;
    boundContext?: string;
    chips: ActionChip[];
}

interface ActionChipsResourceProps {
    resource: {
        type: 'actionChips';
        /** Im Stream steht statt des Inhalts nur diese Referenz. */
        reference?: string;
        data?: ActionChipsContent;
        content?: ActionChipsContent;
    };
    onSubmitInput?: (input: string, prompt?: string, options?: { exclusiveToolId?: string; onAccepted?: () => void }) => void;
    isLastMessage?: boolean;
    stableKey?: string;
    pendingRequest?: boolean;
}

const dismissedActionChips = new Set<string>();

export const ActionChipsResource: React.FC<ActionChipsResourceProps> = ({ resource, onSubmitInput, isLastMessage, stableKey, pendingRequest }) => {
    const { theme } = useCustomerTheme();
    const { t } = useTranslation();

    // Im Stream kommt nur eine Referenz an — der Inhalt wird nachgeladen.
    const content = useResolvedResourceContent<ActionChipsContent>(resource);
    const chips = content?.chips || [];

    const resourceKey = chipResourceKey(stableKey, 'actionChips', chips.map((chip: any) => chip.toolId));
    const [dismissed, setDismissed] = useState(() => dismissedActionChips.has(resourceKey));

    if (!isLastMessage || dismissed || chips.length === 0) return null;

    const handleChipClick = (chip: ActionChip) => {
        // Don't process clicks while a request is pending to avoid hiding the chips
        // before submitInput can actually send the pinned request.
        if (pendingRequest || !onSubmitInput) {
            return;
        }
        // Pin to the exact tool (toolId, not offer) for deterministic routing.
        // Only mark dismissed once submitInput accepts the request — otherwise a
        // still-pending prior turn makes submitInput return immediately and the chips
        // would stay hidden forever via module-level state with no way to retry.
        onSubmitInput(chip.label, chip.prompt, {
            exclusiveToolId: chip.toolId || chip.offer,
            onAccepted: () => {
                setDismissed(true);
                dismissedActionChips.add(resourceKey);
            },
        });
    };

    return (
        <div className="action-chips mb-2 mt-3">
            <div className="mb-2 text-xs font-bold uppercase tracking-[0.5px] text-muted-foreground">
                {t('chatBotPage.nextSteps', 'Nächste Schritte')}
            </div>

            <TooltipProvider delayDuration={300} skipDelayDuration={0}>
                <div className="flex flex-wrap items-center gap-2">
                    {chips.map((chip) => (
                        <Tooltip key={chip.toolId}>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    data-testid="chat-resource-action-chip"
                                    onClick={() => handleChipClick(chip)}
                                    // Die Kundenfarbe kommt zur Laufzeit aus dem Theme — als CSS-Variable
                                    // gesetzt, damit der Hover-Zustand ohne JS-Stilmutation auskommt.
                                    style={{
                                        '--chip-border': `${theme.primaryLight}44`,
                                        '--chip-border-hover': theme.primaryLight,
                                    } as React.CSSProperties}
                                    className="flex max-w-[300px] items-center rounded-[20px] border border-[color:var(--chip-border)] bg-muted px-[14px] py-1.5 text-left text-foreground transition-all duration-200 hover:border-[color:var(--chip-border-hover)] hover:bg-accent hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                                >
                                    <Forward
                                        aria-hidden
                                        className="mr-2 h-3.5 w-3.5 shrink-0"
                                        style={{ color: theme.primaryLight }}
                                    />
                                    <span className="line-clamp-2 text-[13px] leading-[1.4]">
                                        {chip.label}
                                    </span>
                                </button>
                            </TooltipTrigger>
                            <TooltipContent sideOffset={0}>
                                {chip.boundContext ? `${chip.label} · ${chip.boundContext}` : chip.label}
                            </TooltipContent>
                        </Tooltip>
                    ))}

                    <button
                        type="button"
                        data-testid="chat-resource-action-chips-dismiss"
                        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => {
                            setDismissed(true);
                            dismissedActionChips.add(resourceKey);
                        }}
                    >
                        <X aria-hidden className="h-2.5 w-2.5" />
                        {t('global.noThanks', 'Nein danke')}
                    </button>
                </div>
            </TooltipProvider>
        </div>
    );
};
